import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useRef, useState } from "react";
import { formatDate, formatDateTime, formatUsdFull, formatWib } from "@po/shared";
import { api, download, prefetchRecord, qs, type RecordType, type TableResult } from "../lib/api";
import { int } from "../lib/format";
import { readPrefs, writePrefs } from "../lib/prefs";
import { useViewSync, type ViewSyncBody, type ViewSyncState } from "../lib/view-sync";
import { ProgressBar } from "./ProgressBar";
import { useDashboard } from "../pages/Dashboard";
import { Flags, StageBadge } from "./badges";

type Row = Record<string, unknown>;
/** RFQs have no stage: their rows show the Sertica status instead. Addresses, companies and the
 *  plain lists (items, currencies) have neither; a plain row opens no page. */
type Lens = "req" | "po" | "rfq" | "address" | "company" | "plain";
/** Lists synced whole, whose rows are named rather than staged. */
const named = (l: Lens) => l === "address" || l === "company" || l === "plain";

const FLAG_KEYS = [
  "critical",
  "anomaly",
  "openForChanges",
  "urgent",
  "rejectedOnReceipt",
  "overBudget",
] as const;

/** `text` with every case-insensitive match of the search term marked (SPEC §6.3). */
export function Hl({ text, term }: { text: string; term?: string }) {
  if (!term) return <>{text}</>;
  const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((p, i) =>
        i % 2 ? (
          <mark
            key={i}
            className="rounded-[3px] px-0.5 -mx-0.5 bg-[var(--search-hit)] text-inherit"
          >
            {p}
          </mark>
        ) : (
          p
        ),
      )}
    </>
  );
}

const hits = (v: unknown, term: string) =>
  (Array.isArray(v) ? v.join(" ") : typeof v === "string" ? v : "")
    .toLowerCase()
    .includes(term.toLowerCase());

function Cell({
  k,
  type,
  row,
  lens,
  term,
}: {
  k: string;
  type: string;
  row: Row;
  lens: Lens;
  term?: string;
}) {
  const v = row[k];
  if (k === "stage" && (lens === "req" || lens === "po")) {
    const progress =
      lens === "req" && typeof row.linesOpen === "number" && (row.linesOpen as number) > 1
        ? progressText(
            row.stage as string,
            row.linesOpen as number,
            row.linesOrdered as number,
            row.linesReceived as number,
          )
        : null;
    return (
      <span className="inline-flex items-center gap-1.5">
        <StageBadge stage={String(v)} lens={lens} plain />
        {progress && <span className="text-[11px] text-muted whitespace-nowrap">{progress}</span>}
      </span>
    );
  }
  if (k === "estValueUsd") return <EstValue row={row} />;
  // A requisition's status, its lines' workflows beneath (SPEC §8.2).
  if (k === "status" && lens === "req")
    return (
      <span className="block leading-tight">
        <Hl text={String(v ?? "—")} term={term} />
        {row.positionWorkflow != null && (
          <span className="block text-[11.5px] text-muted mt-0.5">
            {String(row.positionWorkflow)}
          </span>
        )}
      </span>
    );
  if (v == null) return <span className="text-muted">—</span>;
  if (type === "usd") return <span className="tnum">{formatUsdFull(Number(v))}</span>;
  if (type === "number") return <span className="tnum">{int(Number(v))}</span>;
  if (type === "date")
    return <span className="tnum whitespace-nowrap">{formatDate(String(v))}</span>;
  if (type === "datetime")
    return <span className="tnum whitespace-nowrap">{formatDateTime(String(v))}</span>;
  if (type === "list")
    return (
      <span className="text-[12px]">
        {(v as string[]).length ? <Hl text={(v as string[]).join(", ")} term={term} /> : "—"}
      </span>
    );
  // Shared PO reads as Sertica's checkbox column; other yes/no columns as text.
  if (k === "shared")
    return (
      <input
        type="checkbox"
        checked={!!v}
        readOnly
        tabIndex={-1}
        aria-label={v ? "Shared" : "Not shared"}
        className="pointer-events-none align-middle"
      />
    );
  if (type === "bool") return v ? <span>Yes</span> : null;
  return (
    <span>
      <Hl text={String(v)} term={term} />
    </span>
  );
}

/** A requisition's estimate, marked when some lines could not be priced (free text, never ordered). */
function EstValue({ row }: { row: Row }) {
  const lines = Number(row.estLines ?? 0);
  const priced = Number(row.estLinesPriced ?? 0);
  const how =
    "Ordered lines at their PO price, the rest at the item's last PO price in the same unit (any count unit — Pieces, Sets, Unit… — for a counted line).";
  if (row.estValueUsd == null)
    return (
      <span className="text-muted" title={lines ? `No line could be priced. ${how}` : how}>
        —
      </span>
    );
  const partial = priced < lines;
  return (
    <span className="tnum whitespace-nowrap" title={`${priced} of ${lines} lines priced. ${how}`}>
      {formatUsdFull(Number(row.estValueUsd))}
      {partial && (
        <span className="text-muted text-[11px]">
          {" "}
          · {priced}/{lines}
        </span>
      )}
    </span>
  );
}

/** "6/10 ordered" before ordering is complete, "3/10 received" after (SPEC §2.1 laggard badge). */
export function progressText(
  stage: string,
  lines: number,
  ordered: number,
  received: number,
): string | null {
  if (lines <= 1) return null;
  if (["ordered", "partly_received", "received"].includes(stage))
    return received > 0 && received < lines ? `${received}/${lines} received` : null;
  return ordered > 0 ? `${ordered}/${lines} ordered` : null;
}

/** Columns folded into the stage and flag cells instead of shown on their own. */
const HIDDEN = new Set<string>([
  "linesOpen",
  "linesOrdered",
  "linesReceived",
  "estLinesPriced",
  "estLines",
  "positionWorkflow",
  ...FLAG_KEYS,
]);

const PAGE_SIZES = [10, 20, 50, 100];

/** The flag chips' column, chosen in the picker like any other. */
const FLAGS_COL = "flags";

/** Columns shown until the user picks their own; a table not listed shows them all. Purchase
 *  orders open on Sertica's own list columns, in its order. */
const DEFAULT_COLUMNS: Record<string, string[]> = {
  // Sertica's six approval rules are in the picker: on TEST only one setup sets any.
  "approval-setups": ["no", "name", "levels", "approverCount", "approvers", "pos", "issuedUsd", "updateDate"],
  "technical-accounts": ["no", "name", "codings", "companies", "requisitionLines", "inactive", "updateDate"],
  requisitions: [
    "no",
    "vessel",
    "description",
    "department",
    "project",
    "status",
    "positions",
    "requestedDeliveryDate",
    "estValueUsd",
    FLAGS_COL,
  ],
  "purchase-orders": [
    "no",
    "description",
    "status",
    "source",
    "supplierNo",
    "supplier",
    "deliveryPlaceNo",
    "deliveryPlace",
    "deliveryDate",
    "orderDate",
    "workflow",
    "companyNo",
    "shared",
    "unitNo",
    "createDate",
  ],
  addresses: [
    "no",
    "name",
    "types",
    "city",
    "country",
    "region",
    "phone",
    "email",
    "categories",
    "issuedUsd",
    "inactive",
  ],
  items: [
    "no",
    "name",
    "groups",
    "unit",
    "manufacturer",
    "referenceNo",
    "drawingNo",
    "inactive",
    FLAGS_COL,
  ],
  units: [
    "no",
    "name",
    "imoNo",
    "vesselType",
    "flag",
    "classSociety",
    "owner",
    "superintendent",
    "groups",
    "source",
    "inactive",
  ],
  users: ["no", "login", "name", "title", "userGroup", "email", "lastLogin", "inactive"],
  certificates: [
    "no",
    "name",
    "vessel",
    "group",
    "status",
    "issueDate",
    "expireDate",
    "nextVerificationDate",
    "overdue",
    "due",
    "location",
  ],
  "certificate-groups": [
    "no",
    "name",
    "parent",
    "subgroups",
    "certificates",
    "rule",
    "locations",
    "defaultResponsible",
    "expireDays",
    "verificationDays",
  ],
};

/** `onRow`: what a row opens instead of a record page, for a plain list. `openSync`: a read from
 *  Sertica each time the list is shown, for a list no sync keeps current (user rights). */
export function DataTable({
  table,
  title,
  lens,
  onRow,
  openSync,
  highlight,
}: {
  table: string;
  title: string;
  lens: Lens;
  onRow?: (r: Row) => void;
  openSync?: ViewSyncBody;
  /** The cells of a row to mark as needing attention (a currency rate the KMK update did not set). */
  highlight?: (r: Row) => string[];
}) {
  const { filters, open, search } = useDashboard();
  // The search box applies here only: the active tab's list (SPEC §6.3).
  const term = search.q?.trim() || undefined;
  const [sort, setSort] = useState<{ key?: string; dir?: "asc" | "desc" }>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const params = { ...filters, q: term, sort: sort.key, dir: sort.dir, page, pageSize };
  const q = useQuery({
    queryKey: ["table", table, params],
    queryFn: () => api<TableResult>(`/tables/${table}${qs(params)}`),
    placeholderData: keepPreviousData,
  });
  const [exporting, setExporting] = useState(false);
  const t = q.data;
  // Back to page 1 when the filters change.
  const fkey = JSON.stringify([filters, term]);
  const [lastF, setLastF] = useState(fkey);
  if (fkey !== lastF) {
    setLastF(fkey);
    setPage(1);
  }
  // On-demand sync (SPEC §6.3): a search fetches its matches from Sertica, then the requisitions or
  // POs on the result page are re-read too; an opened RFQ page always re-reads its RFQs.
  const qc = useQueryClient();
  // The address book, companies and plain lists are synced whole: a search reads only the store.
  const synced = !named(lens);
  // Items are read as they are searched for (SPEC §8.8): the full copy needs scheduled sync.
  const searchSync = useViewSync(
    !term || term.length < 3
      ? null
      : synced
        ? { kind: "search", q: term, table: table as "requisitions" | "purchase-orders" | "rfqs" }
        : table === "items"
          ? { kind: "item-search", q: term }
          : null,
    () => void qc.invalidateQueries({ queryKey: ["table", table] }),
  );
  const searching = !!term && term.length >= 3;
  // The result page is re-read once the number search is in, never the previous search's rows.
  const pageNos =
    t &&
    synced &&
    !q.isPlaceholderData &&
    (lens === "rfq" || (searching && searchSync.status !== "syncing"))
      ? // SEA's and PEA's POs are not in Sertica: only Sertica's are re-read.
        t.rows.filter((r) => r.source == null || r.source === "Sertica").map((r) => String(r.no))
      : [];
  const pageSync = useViewSync(
    !pageNos.length
      ? null
      : lens === "rfq"
        ? { kind: "rfq-page", nos: pageNos }
        : { kind: "page", table: table as "requisitions" | "purchase-orders", nos: pageNos },
    () => void qc.invalidateQueries({ queryKey: ["table", table] }),
  );
  const onOpenSync = useViewSync(
    openSync ?? null,
    () => void qc.invalidateQueries({ queryKey: ["table", table] }),
  );
  const [chosen, setChosen] = useState<string[] | undefined>(() => readPrefs().columns?.[table]);
  const choose = (keys: string[] | undefined) => {
    const { [table]: _, ...rest } = readPrefs().columns ?? {};
    writePrefs({ columns: keys ? { ...rest, [table]: keys } : rest });
    setChosen(keys);
  };
  const allCols = t?.columns.filter((c) => !HIDDEN.has(c.key)) ?? [];
  const flagsAvailable = !!t?.columns.some((c) =>
    FLAG_KEYS.includes(c.key as (typeof FLAG_KEYS)[number]),
  );
  const shown = new Set(
    chosen ?? DEFAULT_COLUMNS[table] ?? [...allCols.map((c) => c.key), FLAGS_COL],
  );
  // The number always shows: it opens the record.
  const cols = allCols.filter((c) => c.key === "no" || shown.has(c.key));
  const hasFlags = flagsAvailable && shown.has(FLAGS_COL);
  const pickable = [
    ...allCols.map((c) => ({ key: c.key, label: c.label })),
    ...(flagsAvailable ? [{ key: FLAGS_COL, label: "Flags" }] : []),
  ];
  const pages = t ? Math.max(1, Math.ceil(t.total / t.pageSize)) : 1;
  const onSort = (key: string) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );
  const recordType: RecordType | null =
    lens === "plain" ? null : lens === "req" ? "requisition" : lens;
  const openRow = (r: Row) =>
    onRow ? onRow(r) : recordType && open({ type: recordType, no: String(r.no) });
  const clickable = !!onRow || !!recordType;
  // A row rested on for a moment loads its page from the store, so the click opens it filled.
  const hover = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(hover.current), []);
  const prefetchRow = (r: Row) => {
    clearTimeout(hover.current);
    if (!recordType) return;
    hover.current = setTimeout(() => void prefetchRecord(qc, recordType, String(r.no)), 150);
  };

  return (
    <section className="border border-line bg-card" aria-label={title}>
      {/* Sertica's navy action bar: the list's name and count, then its actions. */}
      <div className="toolbar flex items-center gap-3 px-3 h-10">
        <h3 className="flex items-center gap-2 text-[13px] font-medium">
          {title}
          {t && (
            <span className="tnum rounded-full bg-white/15 px-2 text-[12px] leading-5">
              {int(t.total)}
            </span>
          )}
        </h3>
        <span className="w-px h-5 bg-white/20" aria-hidden />
        <button
          disabled={exporting}
          onClick={async () => {
            setExporting(true);
            try {
              await download(
                `/export/${table}${qs({ ...filters, q: term, sort: sort.key, dir: sort.dir })}`,
              );
            } finally {
              setExporting(false);
            }
          }}
        >
          <svg
            aria-hidden
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8zM14 3v5h5M12 11v6M9 14l3 3 3-3" />
          </svg>
          {exporting ? "Exporting…" : "Export .xlsx"}
        </button>
        <span className="flex-1" />
        {t && (
          <ColumnPicker
            options={pickable}
            shown={shown}
            onChange={(key, on) =>
              choose(pickable.map((p) => p.key).filter((k) => (k === key ? on : shown.has(k))))
            }
            onReset={() => choose(undefined)}
          />
        )}
      </div>
      <SyncNote
        s={
          onOpenSync.status !== "idle"
            ? onOpenSync
            : searchSync.status === "syncing" || pageSync.status === "idle"
              ? searchSync
              : pageSync
        }
        q={term}
      />
      {/* Desktop and tablet: a table. */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-b border-line">
              {cols.map((c) => (
                <th
                  key={c.key}
                  className="text-left px-3 py-2.5 whitespace-nowrap"
                  aria-sort={
                    t?.sort.key === c.key
                      ? t.sort.dir === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  <button
                    className="col group inline-flex items-center gap-1 hover:text-brand-2"
                    onClick={() => onSort(c.key)}
                  >
                    {c.label}
                    <span
                      aria-hidden
                      className={
                        t?.sort.key === c.key
                          ? "text-ink"
                          : "text-transparent group-hover:text-muted"
                      }
                    >
                      {t?.sort.key === c.key && t.sort.dir === "desc" ? "↓" : "↑"}
                    </span>
                  </button>
                </th>
              ))}
              {hasFlags && <th className="text-left px-3 py-2.5 col">Flags</th>}
            </tr>
          </thead>
          <tbody className={q.isFetching ? "opacity-70" : ""}>
            {/* A number can repeat (a replaced certificate keeps its number): the position keeps keys unique. */}
            {t?.rows.map((r, i) => {
              const marked = highlight?.(r) ?? [];
              return (
                <tr
                  key={`${String(r.no)}:${i}`}
                  className={`border-b border-line hover:bg-hover ${clickable ? "cursor-pointer" : ""}`}
                  onClick={() => openRow(r)}
                  onMouseEnter={() => prefetchRow(r)}
                  onMouseLeave={() => clearTimeout(hover.current)}
                >
                  {cols.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 py-[5px] align-middle ${marked.includes(c.key) ? "cell-warn" : "text-ink2"}`}
                    >
                      {c.key === "no" && !clickable ? (
                        <span className="text-ink">
                          {r.no == null ? "—" : <Hl text={String(r.no)} term={term} />}
                        </span>
                      ) : c.key === "no" ? (
                        <>
                          <button
                            className="text-brand hover:underline"
                            onClick={(e) => (e.stopPropagation(), openRow(r))}
                          >
                            <Hl text={String(r.no)} term={term} />
                          </button>
                          <MatchHint row={r} cols={cols} term={term} />
                        </>
                      ) : (
                        <Cell k={c.key} type={c.type} row={r} lens={lens} term={term} />
                      )}
                    </td>
                  ))}
                  {hasFlags && (
                    <td className="px-3 py-[5px]">
                      <Flags row={r} />
                    </td>
                  )}
                </tr>
              );
            })}
            {t && !t.rows.length && (
              <tr>
                <td colSpan={cols.length + 1} className="px-3 py-6 text-center text-muted">
                  No records match the filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Phone: a card list (SPEC §14). */}
      <ul className="md:hidden divide-y divide-line">
        {t?.rows.map((r, i) => (
          <li key={`${String(r.no)}:${i}`}>
            <button className="w-full text-left p-3 space-y-1" onClick={() => openRow(r)}>
              <div className="flex items-center gap-2">
                <span className="font-medium text-brand">
                  <Hl text={String(r.no)} term={term} />
                </span>
                <span className="flex-1" />
                {lens === "rfq" ? (
                  <span className="text-[12px] text-ink2">{String(r.status ?? "")}</span>
                ) : named(lens) ? (
                  <span className="text-[12px] text-ink2 truncate">{String(r.name ?? "")}</span>
                ) : (
                  <StageBadge stage={String(r.stage ?? "")} lens={lens} plain />
                )}
              </div>
              <div className="text-[12.5px] text-ink2 flex flex-wrap gap-x-3">
                {cols
                  .filter(
                    (c) =>
                      !["no", "stage", "status", ...(named(lens) ? ["name"] : [])].includes(
                        c.key,
                      ) &&
                      r[c.key] != null &&
                      r[c.key] !== false,
                  )
                  .slice(0, 5)
                  .map((c) => (
                    <span key={c.key}>
                      <span className="text-muted">{c.label}: </span>
                      <Cell k={c.key} type={c.type} row={r} lens={lens} term={term} />
                    </span>
                  ))}
              </div>
              <Flags row={r} />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2 px-3 py-2 text-[12.5px] text-muted">
        <span className="flex-1 tnum">
          {t ? (
            `Page ${t.page} of ${pages}`
          ) : q.isError ? (
            <span className="text-[var(--danger)]" role="alert">
              Couldn&apos;t load {title.toLowerCase()}.{" "}
              <button className="underline font-semibold" onClick={() => void q.refetch()}>
                Retry
              </button>
            </span>
          ) : (
            "Loading…"
          )}
        </span>
        <label className="flex items-center gap-1.5">
          Rows per page
          <select
            className="input"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button className="input" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>
        <button className="input" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </div>
    </section>
  );
}

/** "Columns ▾": tick the columns to show, remembered per browser; Reset returns to the default. */
export function ColumnPicker({
  options,
  shown,
  onChange,
  onReset,
  locked = "no",
  className,
}: {
  /** In order; a `group` starts a heading where it first appears. */
  options: { key: string; label: string; group?: string }[];
  shown: Set<string>;
  onChange: (key: string, on: boolean) => void;
  onReset: () => void;
  /** The column that is always shown. */
  locked?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button aria-expanded={open} onClick={() => setOpen((o) => !o)} className={className}>
        Columns{" "}
        <span aria-hidden className="text-[10px]">
          ▼
        </span>
      </button>
      {open && (
        <div
          className="card absolute right-0 z-30 mt-1 w-60 shadow-lg py-1 text-ink text-[13px]"
          role="group"
          aria-label="Columns"
        >
          <div className="max-h-80 overflow-y-auto">
            {options.map((o, i) => (
              <Fragment key={o.key}>
                {o.group && o.group !== options[i - 1]?.group && (
                  <div className="px-3 pt-2 pb-1 text-[11px] text-muted border-t border-line mt-1">
                    {o.group}
                  </div>
                )}
                <label
                  className={`flex items-center gap-2 px-3 py-1 ${o.key === locked ? "text-muted" : "hover:bg-hover cursor-pointer"}`}
                >
                  <input
                    type="checkbox"
                    checked={o.key === locked || shown.has(o.key)}
                    disabled={o.key === locked}
                    onChange={(e) => onChange(o.key, e.target.checked)}
                  />
                  {o.label}
                </label>
              </Fragment>
            ))}
          </div>
          <div className="border-t border-line mt-1 pt-1 px-3">
            <button className="text-brand text-[12.5px] font-medium py-1" onClick={onReset}>
              Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SyncNote({ s, q }: { s: ViewSyncState; q?: string }) {
  if (s.status === "idle") return null;
  if (s.status === "syncing")
    return (
      <div role="status" className="px-3 py-1.5 border-b border-line">
        <ProgressBar
          className="max-w-[320px]"
          progress={s.progress}
          label={
            s.kind === "user-rights"
              ? "Reading the user rights from Sertica (about 20 s)…"
              : s.kind === "search" || s.kind === "item-search"
                ? `Searching Sertica for “${q}”…`
                : s.kind === "page"
                  ? "Updating these results from Sertica…"
                  : "Updating this page from Sertica…"
          }
        />
      </div>
    );
  const bad = s.status === "failed" || s.status === "unavailable";
  return (
    <div
      role="status"
      className={`px-3 py-1.5 border-b border-line text-[12px] ${bad ? "text-[var(--danger)]" : "text-muted"}`}
    >
      {s.status === "done"
        ? `Updated from Sertica at ${formatWib(s.at)}`
        : s.status === "unavailable"
          ? "Sync service is not running: showing stored data"
          : "Couldn’t reach Sertica: showing stored data"}
    </div>
  );
}

/** Why a row matched the search when no visible cell shows it: its first matching item (SPEC §6.3). */
function MatchHint({ row, cols, term }: { row: Row; cols: { key: string }[]; term?: string }) {
  const item = row._match;
  if (!term || typeof item !== "string" || cols.some((c) => hits(row[c.key], term))) return null;
  return (
    <div className="text-[11.5px] text-muted max-w-[220px] truncate" title={item}>
      Item: <Hl text={item} term={term} />
    </div>
  );
}
